import React from 'react';
import { Alert, Button, Progress } from 'antd';
import { IRunningStepProps } from './i-import-wizard';
import {
  CountTag, ERROR_TAG, GREY_TAG, MUTED_TEXT, OK_TAG,
} from './import-tags';
import ProblemTable from './problem-table';

/**
 * Upload → validate → confirm → import, in one step. [AVP-891]
 *
 * The confirmation gate in the middle is the point of the whole redesign:
 * validation writes nothing, so the user sees the real problem list and
 * decides, instead of finding out after half a 200 000-row file has committed.
 */

const BLOCK: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16 };

const megabytes = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const duration = (ms: number): string => {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  return seconds % 60 === 0 ? `${minutes}m` : `${minutes}m ${seconds % 60}s`;
};

const RunningStep = (props: IRunningStepProps): JSX.Element => {
  const {
    job, upload, pollError, problems, problemPage, problemsLoading, downloading, disabled,
    onProblemPage, onDownloadProblems, onConfirm, onCancel, onRetryPoll,
  } = props;

  const problemTable = (
    <ProblemTable
      rows={problems}
      total={job?.errorCount ?? 0}
      truncated={job?.errorTruncated ?? false}
      page={problemPage}
      loading={problemsLoading}
      downloading={downloading}
      onPageChange={onProblemPage}
      onDownload={onDownloadProblems}
    />
  );

  if (pollError) {
    return (
      <div style={BLOCK}>
        <Alert
          type="warning"
          showIcon
          message="Lost contact with the server"
          description={
            // never "the import failed": it is very likely still running
            'The import may still be running. Reopen this window later to check, or try again now.'
          }
        />
        <div>
          <Button onClick={onRetryPoll}>Try again</Button>
        </div>
      </div>
    );
  }

  if (upload && !job) {
    const percent = upload.totalBytes > 0
      ? Math.round((upload.sentBytes / upload.totalBytes) * 100)
      : 0;

    return (
      <div style={BLOCK}>
        <Progress percent={percent} status="active" />
        <div style={MUTED_TEXT}>
          {`Uploading ${megabytes(upload.sentBytes)} of ${megabytes(upload.totalBytes)}`}
          {upload.partCount > 1
            ? ` — part ${upload.partIndex + 1} of ${upload.partCount}`
            : ''}
        </div>
        <div style={MUTED_TEXT}>Keep this window open until the upload finishes.</div>
        <div>
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    );
  }

  if (!job) {
    return <Progress percent={0} status="active" />;
  }

  const keepsRunning = (
    <div style={MUTED_TEXT}>
      You can close this window — the import keeps running on the server.
    </div>
  );

  if (job.state === 'validating') {
    return (
      <div style={BLOCK}>
        {/* no percent until `total` is known: a determinate bar pinned at 0% reads as a hang */}
        <Progress percent={100} status="active" showInfo={false} />
        <div style={{ fontSize: 13 }}>
          {job.processed > 0
            ? `Checking ${job.processed.toLocaleString()} rows…`
            : 'Reading the file…'}
        </div>
        {keepsRunning}
        <div>
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    );
  }

  if (job.state === 'validated') {
    const importable = Math.max(0, job.total - job.errorCount);

    return (
      <div style={BLOCK}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <CountTag count={job.total} label="rows in the file" tone={GREY_TAG} />
          <CountTag count={importable} label="will be imported" tone={OK_TAG} />
          {job.errorCount > 0
            ? <CountTag count={job.errorCount} label="with problems" tone={ERROR_TAG} />
            : null}
        </div>
        <div style={MUTED_TEXT}>Nothing has been written yet.</div>
        {job.errorCount > 0 ? problemTable : null}
      </div>
    );
  }

  if (job.state === 'running') {
    // clamped: the last rows are still committing when processed hits total
    const percent = job.total > 0
      ? Math.min(99, Math.round((job.processed / job.total) * 100))
      : 0;

    return (
      <div style={BLOCK}>
        <Progress percent={percent} status="active" />
        <div style={{ fontSize: 13 }}>
          {`${job.processed.toLocaleString()} of ${job.total.toLocaleString()} rows`}
          {job.estimatedMsRemaining
            ? ` — about ${duration(job.estimatedMsRemaining)} left`
            : ''}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <CountTag count={job.created} label="created" tone={OK_TAG} />
          {job.updated > 0 ? <CountTag count={job.updated} label="updated" tone={OK_TAG} /> : null}
          {job.skipped > 0 ? <CountTag count={job.skipped} label="skipped" tone={GREY_TAG} /> : null}
          {job.failed > 0 ? <CountTag count={job.failed} label="failed" tone={ERROR_TAG} /> : null}
        </div>
        {keepsRunning}
        <div>
          <Button onClick={onCancel} disabled={disabled || job.cancelRequested}>
            {job.cancelRequested ? 'Cancelling…' : 'Cancel'}
          </Button>
        </div>
      </div>
    );
  }

  // terminal, but the user is still looking at this step
  return (
    <div style={BLOCK}>
      {job.message ? <Alert type="info" showIcon message={job.message} /> : null}

      {/*
        * The outcome, before the problems. Without it a run that created
        * 199 505 asset types shows nothing but "495 rows with problems",
        * which after half an hour of waiting reads as a failure.
        */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <CountTag count={job.created} label="created" tone={OK_TAG} />
        {job.updated > 0 ? <CountTag count={job.updated} label="updated" tone={OK_TAG} /> : null}
        {job.skipped > 0 ? <CountTag count={job.skipped} label="skipped" tone={GREY_TAG} /> : null}
        {job.failed > 0 ? <CountTag count={job.failed} label="failed" tone={ERROR_TAG} /> : null}
      </div>

      {job.errorCount > 0 ? problemTable : null}
      <Button type="link" style={{ paddingLeft: 0 }} onClick={onConfirm}>Continue</Button>
    </div>
  );
};

RunningStep.displayName = 'RunningStep';

export default RunningStep;
